import React, { useState, useEffect } from 'react';
import { useEntityStore } from '../store';
import api from '../lib/api';
import { MonthlyOutgoingsView } from './MonthlyOutgoingsView';

/**
 * Monthly Outgoings tab for the Subscriptions surface. Resolves scope from the
 * global entity selector (no filter control of its own, per spec):
 *   - a single entity selected -> that entity's outgoings (slug resolved from
 *     the store's UUID via e.type).
 *   - 'all' selected -> the whole scope (business entities in 'entities' scope,
 *     personal entities in 'personal' scope), with an entity badge per row.
 *
 * Renders the shared MonthlyOutgoingsView, so grouping/money logic is never
 * duplicated. The EUR->GBP scalar is fetched from /fx/rates?base=EUR (rates.GBP
 * is 1 EUR expressed in GBP), matching the portfolio's convertAmount usage.
 */
export function SubscriptionsOutgoingsTab({ scope = 'entities' }) {
  const { selectedEntity, entities } = useEntityStore();
  const [eurGbpRate, setEurGbpRate] = useState(0.85);

  useEffect(() => {
    let active = true;
    api.get('/fx/rates', { params: { base: 'EUR' } })
      .then(({ data }) => {
        if (active) setEurGbpRate(parseFloat(data?.rates?.GBP) || 0.85);
      })
      .catch(() => { /* keep fallback rate */ });
    return () => { active = false; };
  }, []);

  // Resolve the store's selection to the slug the outgoings endpoint expects.
  // 'all' passes through; a group pseudo-entity (group:*) has no single slug,
  // so it falls back to '' and the view shows a "select an entity" prompt.
  const entityId = selectedEntity === 'all'
    ? 'all'
    : (entities.find((e) => e.id === selectedEntity)?.type ?? '');

  return (
    <MonthlyOutgoingsView
      eurGbpRate={eurGbpRate}
      entityId={entityId}
      scope={scope}
      uncoveredLabel="Not set as recurring"
    />
  );
}
